// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Step 4: Prior period adjustments step component.
 *
 * Displays prior period adjustment journal entries for review, segmented per
 * currency. Amounts are never netted across currencies. Includes:
 * - An account balance comparison table showing prior vs current balances
 * - JE8 journal entry table for review
 */
import {
  CloseRunCurrencyBucket,
  JournalEntryWithReviewState,
  PriorPeriodAdjustment,
} from '@/accounting/api/types';
import { JournalEntriesTable } from '@/accounting/components/journal-entries-table';
import { ReviewStatusChip } from '@/accounting/components/review-status-chip';
import { ReviewStepNavigation } from '@/accounting/components/review-step-navigation';
import { usePerCurrencyReview } from '@/accounting/hooks/use-per-currency-review';
import { toPerCurrencySectionBuckets } from '@/accounting/utilities/to-per-currency-section-buckets';
import { PerCurrencySections } from '@/billing/components/currency-section-accordion';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { formatAmountWithCurrency } from '@/currency/utilities/format';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

interface Props {
  entries: JournalEntryWithReviewState[];
  buckets: CloseRunCurrencyBucket[];
  onEntriesChange: (entries: JournalEntryWithReviewState[]) => void;
  onNext: () => void;
  onBack: () => void;
}

/**
 * Returns the appropriate color for a delta value.
 * Positive deltas are shown in error color, negative in success color.
 */
function getDeltaColor(
  delta: string
): 'error.main' | 'success.main' | 'text.primary' {
  const numericDelta = parseFloat(delta);
  if (numericDelta > 0) return 'error.main';
  if (numericDelta < 0) return 'success.main';
  return 'text.primary';
}

/**
 * Renders a row in the account balance comparison table.
 */
function AdjustmentRow(props: {
  adjustment: PriorPeriodAdjustment;
  currencyCode: IsoCurrencyCode;
}) {
  const { adjustment } = props;
  const numericDelta = parseFloat(adjustment.delta);
  const hasNonZeroDelta = numericDelta !== 0;

  return (
    <TableRow>
      <TableCell>{adjustment.accountName}</TableCell>
      <TableCell align="right">
        {formatAmountWithCurrency(adjustment.priorBalance, props.currencyCode, {
          sparse: true,
        })}
      </TableCell>
      <TableCell align="right">
        {formatAmountWithCurrency(
          adjustment.currentBalance,
          props.currencyCode,
          {
            sparse: true,
          }
        )}
      </TableCell>
      <TableCell
        align="right"
        sx={{
          color: getDeltaColor(adjustment.delta),
          fontWeight: hasNonZeroDelta ? 'bold' : 'normal',
        }}
      >
        {formatAmountWithCurrency(adjustment.delta, props.currencyCode, {
          sparse: true,
        })}
      </TableCell>
    </TableRow>
  );
}

/**
 * Renders the account balance comparison table.
 */
function AccountBalanceComparisonTable(props: {
  adjustments: PriorPeriodAdjustment[];
  currencyCode: IsoCurrencyCode;
}) {
  if (props.adjustments.length === 0) {
    return null;
  }

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Account</TableCell>
            <TableCell align="right">
              Locked balance from prior period
            </TableCell>
            <TableCell align="right">
              Computed balance for prior period
            </TableCell>
            <TableCell align="right">Delta</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.adjustments.map((adjustment) => (
            <AdjustmentRow
              key={adjustment.accountType}
              adjustment={adjustment}
              currencyCode={props.currencyCode}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function PriorPeriodAdjustmentsStep(props: Props) {
  const review = usePerCurrencyReview({
    entries: props.entries,
    buckets: props.buckets,
    onEntriesChange: props.onEntriesChange,
    idNamespace: 'prior-period-adjustments-section',
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Card variant="outlined">
        <CardHeader
          title="Prior period adjustments"
          subheader="Review adjustment entries for prior-period deltas"
          sx={{ pt: 3, pb: 1 }}
        />
        <CardContent sx={{ paddingX: 3 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              The system has automatically reconciled the{' '}
              <strong>prior-period balances locked in the prior period</strong>{' '}
              against the <strong>prior-period balances computed today</strong>.
              Any non-zero deltas indicate adjustments needed.
            </Typography>
          </Alert>

          <PerCurrencySections
            buckets={toPerCurrencySectionBuckets(props.buckets)}
            emptyMessage="No prior-period adjustments for this period"
            prominentHeader
            sectionIdPrefix={review.sectionIdPrefix}
            expandedCurrencyCodes={review.expandedCurrencyCodes}
            onToggleSection={review.onToggleSection}
            renderHeaderChip={(bucket) => (
              <ReviewStatusChip
                isReviewed={review.sectionPendingCount(bucket.currency) === 0}
              />
            )}
            renderBucket={(bucket) => {
              const currencyEntries = props.entries.filter(
                (entry) => entry.currency === bucket.currency
              );
              const bucketHasAdjustments = currencyEntries.some(
                (entry) => entry.lineItems.length > 0
              );

              return (
                <>
                  <AccountBalanceComparisonTable
                    adjustments={bucket.adjustments}
                    currencyCode={bucket.currency}
                  />

                  {bucketHasAdjustments ? (
                    <>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        paragraph
                      >
                        The following adjustment entry reconciles the
                        prior-period deltas.
                      </Typography>
                      <JournalEntriesTable
                        entries={currencyEntries}
                        onMarkReviewed={review.handleMarkReviewed}
                        currencyCode={bucket.currency}
                        flashEntryId={review.flashEntryId}
                        onFlashComplete={review.onFlashComplete}
                      />
                    </>
                  ) : (
                    <Alert severity="success">
                      No prior-period adjustments required
                    </Alert>
                  )}
                </>
              );
            }}
          />
        </CardContent>
      </Card>

      <ReviewStepNavigation
        onBack={props.onBack}
        onNext={props.onNext}
        continueLabel="Continue to lock period"
        allReviewed={review.allReviewed}
        onContinueBlocked={review.onContinueBlocked}
      />
    </Box>
  );
}
